/**
 * Amazon Ads — Sponsored Display v3 Column Map (Story 19.15, Task 4)
 *
 * Single source of truth for the 30 priority SD metrics + 4 SD-only dimensions
 * defined in Story 19.15 AC #5. Every entry uses the EXACT API field name from
 * `_bmad/bmm/data/integration-metrics/amazon-ads.md § Sponsored Display`.
 *
 * **Defensive contract:** if Amazon ever renames a column, the
 * `amazon_ads_column_drift_detected` warning fires at runtime AND the v2→v3
 * golden snapshot test (sd-column-map.test.ts) fails in CI per AC #5.
 *
 * **Round 2 Pre-mortem E — `viewabilityRate` denominator:** Amazon's
 * `viewableImpressions / measurableImpressions` (NOT `/ impressions`). The
 * adapter does NOT derive — emit Amazon's value directly. SD is the most
 * footgun-laden surface because of click+view splits across nearly every
 * conversion metric (Round 3 Vuln 4 — typed column-map keys per ad-product).
 *
 * Out of scope (ships in 19.17 with widget presets): `sdAdGroups`,
 * `sdAdGroupsByMatchedTarget`, `sdTargeting`, `sdGrossAndInvalids`. SD keyword
 * is NOT supported by Amazon (Discussion #165 — verified May 2026).
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

// ============================================================================
// Report-type union (3 SD report-type IDs supported in 19.15)
// ============================================================================

/**
 * Story 19.16.1 AC #3 — extended to 6 SD report-type IDs:
 *   - 19.15: sdCampaigns / sdAdvertisedProduct / sdPurchasedProduct
 *   - 19.16.1: sdAdGroups / sdAdGroupsByMatchedTarget / sdTargeting
 *
 * Sponsored Display does NOT have Rufus prompts as of May 2026 — only SP+SB
 * have prompts (GA March 25 2026), shipped as DIMENSION extensions. The prior
 * 19.16.1 draft included `sdRufusPrompts` as a defensive guess; that report
 * type does NOT exist on Amazon's API and is dropped.
 *
 * Frozen ADD-ONLY policy.
 */
export const SD_REPORT_TYPE_IDS = [
  "sdCampaigns",
  "sdAdvertisedProduct",
  "sdPurchasedProduct",
  "sdAdGroups",
  "sdAdGroupsByMatchedTarget",
  "sdTargeting",
] as const;

export type SdReportTypeId = (typeof SD_REPORT_TYPE_IDS)[number];

// ============================================================================
// Metric column map (AC #5 — 30 priority metrics)
// ============================================================================

export type SdMetricDataType =
  | "Integer"
  | "Currency"
  | "Percentage"
  | "Ratio"
  | "Decimal";

export interface SdMetricColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: SdReportTypeId;
  readonly columnName: string;
  readonly dataType: SdMetricDataType;
  /**
   * Story 19.15 baseline + Story 35.7 extension — Sponsored Display uniformly
   * uses 14d for conversion-attributed metrics. `undefined` for delivery-only
   * fields (see `SD_DELIVERY_ONLY_KEYS`) which have no attribution window —
   * they aggregate impressions/users across the date range.
   */
  readonly attributionWindowDays?: 14;
  readonly compatibleWidgets: readonly string[];
  /** Story 31-31 — aggregation semantics, carried into the MetricDefinition projection. */
  readonly aggregationKind?: MetricDefinition["aggregationKind"];
  readonly ratioComponents?: MetricDefinition["ratioComponents"];
}

/**
 * Story 35.7 (Code Review Round 1) — single source-of-truth for SD metric
 * keys that omit `attributionWindowDays` because they're delivery-only
 * aggregations (no attribution model applies). Used by the `attributionWindowDays
 * === 14` invariant test to allowlist exceptions; future delivery-only
 * metrics MUST be appended here AND in the column-map.
 */
export const SD_DELIVERY_ONLY_KEYS = [
  "cumulativeReach",
  "impressionsFrequencyAverage",
] as const;

/**
 * The 30 priority SD metrics ("view-through retargeting golden path") chosen
 * via Round 4 stakeholder QA architect's prioritization. Order matches AC #5.
 *
 * Categories:
 *   - SD Core Delivery (5): impressions, impressionsViews, clicks, cost, viewabilityRate
 *   - SD Purchases — Click + View Split (3): purchases, purchasesClicks, purchasesPromotedClicks
 *   - SD Sales — Click + View Split (3): sales, salesClicks, salesPromotedClicks
 *   - SD Units Sold (2): unitsSold, unitsSoldClicks
 *   - SD Add to Cart Family (5): addToCart, addToCartClicks, addToCartViews, addToCartRate, eCPAddToCart
 *   - SD New-To-Brand DPV (4): NTB DPV + clicks + views + rate
 *   - SD Branded Search Family (3): brandedSearches, brandedSearchesClicks, brandedSearchesViews
 *   - SD View CTR + DPV (2): viewClickThroughRate, detailPageViews
 *   - SD Brand Halo (3): salesBrandHalo, unitsSoldBrandHalo, conversionsBrandHalo
 */
export const SD_V3_COLUMN_MAP: Record<string, SdMetricColumnEntry> = {
  // --- Core Delivery (5) ---
  impressions: {
    key: "impressions",
    label: "Impressions",
    reportTypeId: "sdCampaigns",
    columnName: "impressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  impressionsViews: {
    key: "impressionsViews",
    label: "Impressions (Views)",
    reportTypeId: "sdCampaigns",
    columnName: "impressionsViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  clicks: {
    key: "clicks",
    label: "Clicks",
    reportTypeId: "sdCampaigns",
    columnName: "clicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  cost: {
    key: "cost",
    label: "Cost (Spend)",
    reportTypeId: "sdCampaigns",
    columnName: "cost",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  viewabilityRate: {
    key: "viewabilityRate",
    label: "Viewability Rate",
    reportTypeId: "sdCampaigns",
    columnName: "viewabilityRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "viewableImpressions", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // --- Purchases — Click + View Split (3) ---
  // The most important SD distinction from SP — view-through attribution.
  purchases: {
    key: "purchases",
    label: "Purchases (Total)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "purchases",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  purchasesClicks: {
    key: "purchasesClicks",
    label: "Purchases (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "purchasesClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  purchasesPromotedClicks: {
    key: "purchasesPromotedClicks",
    label: "Purchases (Promoted Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "purchasesPromotedClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // --- Sales — Click + View Split (3) ---
  sales: {
    key: "sales",
    label: "Sales (Total)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "sales",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  salesClicks: {
    key: "salesClicks",
    label: "Sales (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "salesClicks",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  salesPromotedClicks: {
    key: "salesPromotedClicks",
    label: "Sales (Promoted Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "salesPromotedClicks",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // --- Units Sold (2) ---
  unitsSold: {
    key: "unitsSold",
    label: "Units Sold (Total)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "unitsSold",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  unitsSoldClicks: {
    key: "unitsSoldClicks",
    label: "Units Sold (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "unitsSoldClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // --- Add to Cart Family (5) ---
  addToCart: {
    key: "addToCart",
    label: "Add to Cart (Total)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToCart",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  addToCartClicks: {
    key: "addToCartClicks",
    label: "Add to Cart (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToCartClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  addToCartViews: {
    key: "addToCartViews",
    label: "Add to Cart (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToCartViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  addToCartRate: {
    key: "addToCartRate",
    label: "Add to Cart Rate",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToCartRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "addToCart", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  eCPAddToCart: {
    key: "eCPAddToCart",
    label: "Effective CP Add to Cart",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "eCPAddToCart",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "addToCart" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart"],
  },

  // --- NTB Detail Page Views (4) ---
  newToBrandDetailPageViews: {
    key: "newToBrandDetailPageViews",
    label: "NTB Detail Page Views (Total)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandDetailPageViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  newToBrandDetailPageViewClicks: {
    key: "newToBrandDetailPageViewClicks",
    label: "NTB DPV (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandDetailPageViewClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  newToBrandDetailPageViewViews: {
    key: "newToBrandDetailPageViewViews",
    label: "NTB DPV (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandDetailPageViewViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  newToBrandDetailPageViewRate: {
    key: "newToBrandDetailPageViewRate",
    label: "NTB DPV Rate",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandDetailPageViewRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "newToBrandDetailPageViews", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge"],
  },

  // --- Branded Search Family (3) ---
  // Round 3 Vuln 4 — distinct keys per ad-product. SD `brandedSearches` is
  // distinct from SB's despite the identical column name (different report
  // surface = different attribution semantics).
  brandedSearches: {
    key: "brandedSearches",
    label: "Branded Searches",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "brandedSearches",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  brandedSearchesClicks: {
    key: "brandedSearchesClicks",
    label: "Branded Searches (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "brandedSearchesClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  brandedSearchesViews: {
    key: "brandedSearchesViews",
    label: "Branded Searches (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "brandedSearchesViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },

  // --- View CTR + DPV (2) ---
  viewClickThroughRate: {
    key: "viewClickThroughRate",
    label: "View Click-Through Rate",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "viewClickThroughRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressionsViews" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  detailPageViews: {
    key: "detailPageViews",
    label: "Detail Page Views",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "detailPageViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // --- Brand Halo (3) ---
  salesBrandHalo: {
    key: "salesBrandHalo",
    label: "Brand Halo Sales",
    reportTypeId: "sdPurchasedProduct",
    columnName: "salesBrandHalo",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  unitsSoldBrandHalo: {
    key: "unitsSoldBrandHalo",
    label: "Brand Halo Units Sold",
    reportTypeId: "sdPurchasedProduct",
    columnName: "unitsSoldBrandHalo",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  conversionsBrandHalo: {
    key: "conversionsBrandHalo",
    label: "Brand Halo Conversions",
    reportTypeId: "sdPurchasedProduct",
    columnName: "conversionsBrandHalo",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },

  // ==========================================================================
  // Story 19.16.1 AC #3 — 30 NEW SD metrics across 3 NEW report-type IDs
  // ==========================================================================

  // ----- sdAdGroups (8) -----
  // Story 31-31 Code Review: adGroupId/Name/Status/BidStrategy are identity/
  // setting FACTS (dataType "Integer" is a pre-existing 19.16.1 mislabel for
  // the string columns) — summing or averaging them is meaningless, so they
  // carry NO aggregationKind and live in the test's
  // NON_NUMERIC_FACT_EXEMPTIONS (31-30 must not numerically aggregate them).
  adGroupId: {
    key: "adGroupId",
    label: "SD Ad Group ID",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupId",
    dataType: "Integer",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  adGroupName: {
    key: "adGroupName",
    label: "SD Ad Group Name",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupName",
    dataType: "Integer",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  adGroupStatus: {
    key: "adGroupStatus",
    label: "SD Ad Group Status",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupStatus",
    dataType: "Integer",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  adGroupBidStrategy: {
    key: "adGroupBidStrategy",
    label: "SD Ad Group Bid Strategy",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupBidStrategy",
    dataType: "Integer",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  adGroupImpressions: {
    key: "adGroupImpressions",
    label: "SD Ad Group Impressions",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  adGroupClicks: {
    key: "adGroupClicks",
    label: "SD Ad Group Clicks",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  adGroupCost: {
    key: "adGroupCost",
    label: "SD Ad Group Cost",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupCost",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  adGroupPurchases14d: {
    key: "adGroupPurchases14d",
    label: "SD Ad Group Purchases (14d)",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },

  // ----- sdAdGroupsByMatchedTarget (12) -----
  matchedTargetCategory: {
    key: "matchedTargetCategory",
    label: "Matched Target Category",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetCategory",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  matchedTargetAsin: {
    key: "matchedTargetAsin",
    label: "Matched Target ASIN",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetAsin",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  matchedTargetExpression: {
    key: "matchedTargetExpression",
    label: "Matched Target Expression",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetExpression",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  matchedTargetImpressions: {
    key: "matchedTargetImpressions",
    label: "Matched Target Impressions",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  matchedTargetClicks: {
    key: "matchedTargetClicks",
    label: "Matched Target Clicks",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  matchedTargetCost: {
    key: "matchedTargetCost",
    label: "Matched Target Cost",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetCost",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  matchedTargetCTR: {
    key: "matchedTargetCTR",
    label: "Matched Target CTR",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetCTR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "matchedTargetClicks", denominator: "matchedTargetImpressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  matchedTargetCPC: {
    key: "matchedTargetCPC",
    label: "Matched Target CPC",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetCPC",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "matchedTargetCost", denominator: "matchedTargetClicks" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  matchedTargetPurchases14d: {
    key: "matchedTargetPurchases14d",
    label: "Matched Target Purchases (14d)",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  matchedTargetSales14d: {
    key: "matchedTargetSales14d",
    label: "Matched Target Sales (14d)",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  matchedTargetROAS14d: {
    key: "matchedTargetROAS14d",
    label: "Matched Target ROAS (14d)",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "matchedTargetSales14d", denominator: "matchedTargetCost" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  matchedTargetACOS14d: {
    key: "matchedTargetACOS14d",
    label: "Matched Target ACOS (14d)",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTargetACOS14d",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "matchedTargetCost", denominator: "matchedTargetSales14d" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // ----- sdTargeting (10) -----
  // Story 31-31 Code Review: targeting-type is a SETTING fact (string column,
  // pre-existing "Integer" mislabel) — no aggregationKind; listed in
  // NON_NUMERIC_FACT_EXEMPTIONS.
  targetingType: {
    key: "targetingType",
    label: "Targeting Type",
    reportTypeId: "sdTargeting",
    columnName: "targetingType",
    dataType: "Integer",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  targetingExpressionDetail: {
    key: "targetingExpressionDetail",
    label: "Targeting Expression Detail",
    reportTypeId: "sdTargeting",
    columnName: "targetingExpressionDetail",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["data-table"],
  },
  targetingImpressions: {
    key: "targetingImpressions",
    label: "Targeting Impressions",
    reportTypeId: "sdTargeting",
    columnName: "targetingImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  targetingClicks: {
    key: "targetingClicks",
    label: "Targeting Clicks",
    reportTypeId: "sdTargeting",
    columnName: "targetingClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  targetingCost: {
    key: "targetingCost",
    label: "Targeting Cost",
    reportTypeId: "sdTargeting",
    columnName: "targetingCost",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  targetingCTR: {
    key: "targetingCTR",
    label: "Targeting CTR",
    reportTypeId: "sdTargeting",
    columnName: "targetingCTR",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "targetingClicks", denominator: "targetingImpressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  targetingCPC: {
    key: "targetingCPC",
    label: "Targeting CPC",
    reportTypeId: "sdTargeting",
    columnName: "targetingCPC",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "targetingCost", denominator: "targetingClicks" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  targetingPurchases14d: {
    key: "targetingPurchases14d",
    label: "Targeting Purchases (14d)",
    reportTypeId: "sdTargeting",
    columnName: "targetingPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  targetingSales14d: {
    key: "targetingSales14d",
    label: "Targeting Sales (14d)",
    reportTypeId: "sdTargeting",
    columnName: "targetingSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  targetingROAS14d: {
    key: "targetingROAS14d",
    label: "Targeting ROAS (14d)",
    reportTypeId: "sdTargeting",
    columnName: "targetingROAS14d",
    dataType: "Ratio",
    aggregationKind: "ratio", ratioComponents: { numerator: "targetingSales14d", denominator: "targetingCost" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },

  // Story 19.16.1 AC #11 — SD AllViews baseline columns. SD never had blind
  // view-through pre-2026; this is a no-op for column-shape consistency.
  purchasesAllViews14d: {
    key: "purchasesAllViews14d",
    label: "Purchases — All Views (14d)",
    reportTypeId: "sdCampaigns",
    columnName: "purchasesAllViews14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  salesAllViews14d: {
    key: "salesAllViews14d",
    label: "Sales — All Views (14d)",
    reportTypeId: "sdCampaigns",
    columnName: "salesAllViews14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },

  // ==========================================================================
  // Story 35.7 AC #3 — SD Reach + ATL + Qualified Borrows + Lead Gen + NTB
  // long-tail (+17 metrics). All existing report types (sdCampaigns +
  // sdAdvertisedProduct + sdPurchasedProduct) — no new report-type IDs.
  // Verified May 2026 via Amazon Ads v3 SD OpenAPI + Airbyte source-amazon-ads
  // SD report manifest + Amazon SD Lead Gen GA Jan 2026 blog.
  // ==========================================================================
  // SD Reach (2 — sdCampaigns). Delivery-only — no attribution window
  // (impressions/users aggregated across date range, NOT click/view-attributed).
  cumulativeReach: {
    key: "cumulativeReach",
    label: "Cumulative Reach",
    reportTypeId: "sdCampaigns",
    columnName: "cumulativeReach",
    dataType: "Integer",
    aggregationKind: "additive",
    // attributionWindowDays intentionally undefined (delivery-only metric).
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  impressionsFrequencyAverage: {
    key: "impressionsFrequencyAverage",
    label: "Impressions Frequency Average",
    reportTypeId: "sdCampaigns",
    columnName: "impressionsFrequencyAverage",
    dataType: "Decimal",
    aggregationKind: "ratio", ratioComponents: { numerator: "impressions", denominator: "cumulativeReach" },
    // attributionWindowDays intentionally undefined (delivery-only metric).
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  // SD Add to List (3 — sdAdvertisedProduct).
  addToList: {
    key: "addToList",
    label: "Add to List",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToList",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  addToListFromClicks: {
    key: "addToListFromClicks",
    label: "Add to List (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToListFromClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  addToListFromViews: {
    key: "addToListFromViews",
    label: "Add to List (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "addToListFromViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  // SD Qualified Borrows — Kindle/Prime Reading (3 — sdAdvertisedProduct).
  // KU + Prime Reading + Kindle Unlimited eligible-titles only; ZERO usage for
  // non-Kindle advertisers but valuable for KDP authors + KDP agencies.
  qualifiedBorrows: {
    key: "qualifiedBorrows",
    label: "Qualified Borrows",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "qualifiedBorrows",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  qualifiedBorrowsFromClicks: {
    key: "qualifiedBorrowsFromClicks",
    label: "Qualified Borrows (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "qualifiedBorrowsFromClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "data-table"],
  },
  qualifiedBorrowsFromViews: {
    key: "qualifiedBorrowsFromViews",
    label: "Qualified Borrows (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "qualifiedBorrowsFromViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "data-table"],
  },
  // SD Royalty Qualified Borrows (3 — sdAdvertisedProduct). Stricter subset:
  // Amazon paid the author royalty for the read (royalty-qualified borrow =
  // "this advertising actually paid the author").
  royaltyQualifiedBorrows: {
    key: "royaltyQualifiedBorrows",
    label: "Royalty Qualified Borrows",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "royaltyQualifiedBorrows",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  royaltyQualifiedBorrowsFromClicks: {
    key: "royaltyQualifiedBorrowsFromClicks",
    label: "Royalty Qualified Borrows (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "royaltyQualifiedBorrowsFromClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "data-table"],
  },
  royaltyQualifiedBorrowsFromViews: {
    key: "royaltyQualifiedBorrowsFromViews",
    label: "Royalty Qualified Borrows (View-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "royaltyQualifiedBorrowsFromViews",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "data-table"],
  },
  // SD Lead Generation (3 — sdAdvertisedProduct). GA across NA+EU Jan 2026;
  // FE still beta as of May 2026. Null-tolerant per AC #3: accounts without
  // Lead Gen campaigns return `null` for these fields (NOT 0), preserving the
  // "no campaigns of this type" semantic — Story 19.14 Round 3 Vuln 3
  // `Number.isFinite()` guard surfaces null as null, NOT coerced to 0.
  leadFormOpens: {
    key: "leadFormOpens",
    label: "Lead Form Opens",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "leadFormOpens",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  leads: {
    key: "leads",
    label: "Leads",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "leads",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  linkOuts: {
    key: "linkOuts",
    label: "Link Outs",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "linkOuts",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  // SD Branded Search Performance (2 — sdAdvertisedProduct).
  // Derived ratios emitted verbatim by Amazon — adapter does NOT compute.
  brandedSearchRate: {
    key: "brandedSearchRate",
    label: "Branded Search Rate",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "brandedSearchRate",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "brandedSearches", denominator: "impressions" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
  },
  eCPBrandSearch: {
    key: "eCPBrandSearch",
    label: "eCP Brand Search",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "eCPBrandSearch",
    dataType: "Currency",
    aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "brandedSearches" },
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
  // SD NTB Units Sold (2 — sdAdvertisedProduct). Catalog rows #122-#123.
  // Sister metrics to SB's `newToBrandUnitsSold14d` (Story 19.15) — the SD
  // equivalent omits the 14d suffix because SD reports use 14d uniformly
  // (Round 2 Pre-mortem D — implicit 14d attribution window).
  newToBrandUnitsSold: {
    key: "newToBrandUnitsSold",
    label: "New To Brand Units Sold",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandUnitsSold",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
  },
  newToBrandUnitsSoldClicks: {
    key: "newToBrandUnitsSoldClicks",
    label: "New To Brand Units Sold (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "newToBrandUnitsSoldClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "data-table"],
  },
  // SD Detail Page Views Click split (1 — sdAdvertisedProduct).
  // catalog row #101 — was `gap` even though #100 `detailPageViews` was flipped
  // to `live` in 19.15.
  //
  // **Cascade priority caveat** (Code Review Round 1): the key
  // `detailPageViewsClicks` ALSO exists in `SB_V3_COLUMN_MAP` (Story 19.15);
  // the SP→SB→SD→DSP cascade in `groupMetricsByAdProduct` matches SB first,
  // so a fetch request for this key alone routes to SB. To force SD dispatch,
  // callers must supply `ctx.requestedLevel === "amazon-sd"` (the surface
  // hint used by the multi-product fetchers). The two adapters return
  // identical-shaped data for this key from different report types
  // (sbCampaigns vs sdAdvertisedProduct).
  detailPageViewsClicks: {
    key: "detailPageViewsClicks",
    label: "Detail Page Views (Click-Through)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "detailPageViewsClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: ["kpi", "line-chart", "data-table"],
  },
};

// ============================================================================
// Dimension column map (AC #12)
// ============================================================================

export interface SdDimensionColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: SdReportTypeId | "any";
  readonly columnName: string;
  readonly groupByValue: string;
}

/**
 * 4 NEW SD-specific dimensions per AC #12 (campaign* dimensions are reused
 * from SP map — the "ANY" bucket).
 */
export const SD_V3_DIMENSION_MAP: Record<string, SdDimensionColumnEntry> = {
  adId: {
    key: "adId",
    label: "Ad ID (SD)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "adId",
    groupByValue: "advertiser",
  },
  promotedAsin: {
    key: "promotedAsin",
    label: "Promoted ASIN",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "promotedAsin",
    groupByValue: "advertiser",
  },
  promotedSku: {
    key: "promotedSku",
    label: "Promoted SKU",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "promotedSku",
    groupByValue: "advertiser",
  },
  targetingExpression: {
    key: "targetingExpression",
    label: "Targeting Expression",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "targetingExpression",
    groupByValue: "matchedTarget",
  },
  // Story 19.16.1 AC #3 — sdAdGroups dimensions
  sdAdGroupId: {
    key: "sdAdGroupId",
    label: "SD Ad Group ID (dim)",
    reportTypeId: "sdAdGroups",
    columnName: "adGroupId",
    groupByValue: "adGroup",
  },
  // sdAdGroupsByMatchedTarget dimensions
  matchedTarget: {
    key: "matchedTarget",
    label: "Matched Target",
    reportTypeId: "sdAdGroupsByMatchedTarget",
    columnName: "matchedTarget",
    groupByValue: "matchedTarget",
  },
  // sdTargeting dimensions
  sdTargetingType: {
    key: "sdTargetingType",
    label: "SD Targeting Type (dim)",
    reportTypeId: "sdTargeting",
    columnName: "targetingType",
    groupByValue: "targetingType",
  },
  // Story 35.7 AC #7 (Code Review Round 3) — Portfolio + Campaign Budget
  // cluster shared across SP/SB/SD. SD's picker (`pickSdReportType`) accepts
  // these as `ANY_DIMS`; the dim-map registration here lets `buildSdColumnList`
  // resolve them to wire-format column names rather than silently dropping.
  // Verified May 2026 via Amazon Ads v3 SD OpenAPI — these columns surface on
  // all SD campaign-level reports.
  portfolioId: {
    key: "portfolioId",
    label: "Portfolio ID",
    reportTypeId: "any",
    columnName: "portfolioId",
    groupByValue: "campaign",
  },
  campaignBudgetAmount: {
    key: "campaignBudgetAmount",
    label: "Campaign Budget Amount",
    reportTypeId: "any",
    columnName: "campaignBudgetAmount",
    groupByValue: "campaign",
  },
  campaignBudgetCurrencyCode: {
    key: "campaignBudgetCurrencyCode",
    label: "Campaign Budget Currency Code",
    reportTypeId: "any",
    columnName: "campaignBudgetCurrencyCode",
    groupByValue: "campaign",
  },
  campaignRuleBasedBudgetAmount: {
    key: "campaignRuleBasedBudgetAmount",
    label: "Campaign Rule-Based Budget Amount",
    reportTypeId: "any",
    columnName: "campaignRuleBasedBudgetAmount",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleId: {
    key: "campaignApplicableBudgetRuleId",
    label: "Campaign Applicable Budget Rule ID",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleId",
    groupByValue: "campaign",
  },
  campaignApplicableBudgetRuleName: {
    key: "campaignApplicableBudgetRuleName",
    label: "Campaign Applicable Budget Rule Name",
    reportTypeId: "any",
    columnName: "campaignApplicableBudgetRuleName",
    groupByValue: "campaign",
  },
  biddingStrategy: {
    key: "biddingStrategy",
    label: "Campaign Bidding Strategy",
    reportTypeId: "any",
    columnName: "biddingStrategy",
    groupByValue: "campaign",
  },
  campaignBudgetType: {
    key: "campaignBudgetType",
    label: "Campaign Budget Type",
    reportTypeId: "any",
    columnName: "campaignBudgetType",
    groupByValue: "campaign",
  },
  // Story 35.7 AC #7 — SD Lead Gen-specific dimensions (3). Per Code Review
  // Round 1: these MUST be in the per-product dim map to route via
  // `buildSdColumnList`. Surface only when Lead Gen campaigns exist;
  // null-tolerant per AC #3.
  leadFormId: {
    key: "leadFormId",
    label: "Lead Form ID (SD)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "leadFormId",
    groupByValue: "advertiser",
  },
  leadFormType: {
    key: "leadFormType",
    label: "Lead Form Type (SD)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "leadFormType",
    groupByValue: "advertiser",
  },
  leadFormSchemaVersion: {
    key: "leadFormSchemaVersion",
    label: "Lead Form Schema Version (SD)",
    reportTypeId: "sdAdvertisedProduct",
    columnName: "leadFormSchemaVersion",
    groupByValue: "advertiser",
  },
};

// ============================================================================
// Public arrays (consumed by getAvailableMetrics / getAvailableDimensions)
// ============================================================================

/** AC #5 — exactly 30 priority SD metrics. */
export const AMAZON_ADS_SD_METRICS: MetricDefinition[] = Object.values(
  SD_V3_COLUMN_MAP
).map((m) => ({
  key: m.key,
  label: m.label,
  type:
    m.dataType === "Currency"
      ? "currency"
      : m.dataType === "Percentage"
        ? "percentage"
        : "number",
  category: "Amazon Ads SD",
  dataType: m.dataType,
  // Story 31-31 — carry aggregation metadata through the projection.
  ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
  ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
  compatibleWidgets: [...m.compatibleWidgets],
  apiField: m.columnName,
}));

/** AC #12 — 4 NEW SD-only dimensions. */
export const AMAZON_ADS_SD_DIMENSIONS: DimensionDefinition[] = Object.values(
  SD_V3_DIMENSION_MAP
).map((d) => ({
  key: d.key,
  label: d.label,
  type:
    d.key === "promotedAsin" || d.key === "promotedSku"
      ? "Product"
      : d.key === "targetingExpression"
        ? "Search"
        : "Structure",
}));

// ============================================================================
// Pure helper: pick report type from dimensions (AC #1)
// ============================================================================

export type PickSdReportTypeResult =
  | {
      readonly ok: true;
      readonly reportTypeId: SdReportTypeId;
      readonly warnings: string[];
    }
  | {
      readonly ok: false;
      readonly error:
        | "unsupported_dimension_combination"
        | "ad_product_dimension_mismatch";
      readonly message: string;
    };

/**
 * Deterministic pure function that maps a requested dimension set to the
 * correct SD report type. Order:
 *
 *   1. `purchasedAsin` ∈ dim → sdPurchasedProduct
 *   2. `promotedAsin`/`promotedSku`/`adId`/`targetingExpression` ∈ dim →
 *      sdAdvertisedProduct
 *   3. dim ⊆ {campaign*} → sdCampaigns
 *   4. SB-only dim (`adGroupId`, `adGroupName`, `attributionType`) →
 *      ad_product_dimension_mismatch
 *   5. SP-only dim (`advertisedAsin`, `advertisedSku`) →
 *      ad_product_dimension_mismatch
 *   6. otherwise → unsupported_dimension_combination
 *
 * Empty dimension array defaults to sdCampaigns.
 */
export function pickSdReportType(
  dimensions: ReadonlyArray<string>
): PickSdReportTypeResult {
  const set = new Set(dimensions);
  const warnings: string[] = [];

  const ANY_DIMS = new Set([
    "campaignId",
    "campaignName",
    "campaignStatus",
    // Story 35.7 (Code Review Round 2) — Portfolio + Campaign Budget cluster
    // applies to SD campaigns identically to SP/SB (verified May 2026 via
    // Amazon Ads v3 SD OpenAPI). Mirrors the SP ANY_DIMS extension to close
    // the dispatch-routing gap surfaced by Round 2.
    "portfolioId",
    "campaignBudgetAmount",
    "campaignBudgetCurrencyCode",
    "campaignRuleBasedBudgetAmount",
    "campaignApplicableBudgetRuleId",
    "campaignApplicableBudgetRuleName",
    "biddingStrategy",
    "campaignBudgetType",
  ]);
  const PURCHASED_PRODUCT_DIMS = new Set(["purchasedAsin"]);
  // Story 35.7 (Code Review Round 2) — Lead Gen-specific dims route to
  // `sdAdvertisedProduct` (the report type that surfaces Lead Gen campaigns).
  const LEAD_GEN_DIMS = new Set([
    "leadFormId",
    "leadFormType",
    "leadFormSchemaVersion",
  ]);
  const ADV_PRODUCT_DIMS = new Set([
    "promotedAsin",
    "promotedSku",
    "adId",
    "targetingExpression",
  ]);
  // Story 19.16.1 AC #3 — 3 NEW SD report-type dimension buckets.
  const SD_AD_GROUPS_DIMS = new Set(["sdAdGroupId"]);
  const SD_AD_GROUPS_BY_MATCHED_TARGET_DIMS = new Set(["matchedTarget"]);
  const SD_TARGETING_DIMS = new Set(["sdTargetingType"]);
  // Round 3 Vuln 4 — typed column-map keys per ad-product prevent silent
  // mis-routing across surfaces.
  // Note: `adGroupId` / `adGroupName` are now valid on SD via the new
  // `sdAdGroups` report-type, so they're moved out of SB_ONLY_DIMS.
  const SB_ONLY_DIMS = new Set(["attributionType"]);
  const SP_ONLY_DIMS = new Set([
    "advertisedAsin",
    "advertisedSku",
    "placement",
    "targeting",
    "keyword",
    "matchType",
    // Story 35.7 (Code Review Round 2) — the 35.7 SP Keyword family is
    // SP-exclusive on `spTargeting` reports; SD does NOT expose these.
    "keywordId",
    "keywordType",
    "keywordBid",
    "targetingId",
    "targetingText",
    "searchTerm",
  ]);

  const buckets = new Set<SdReportTypeId>();
  const unknown: string[] = [];
  for (const d of set) {
    if (ANY_DIMS.has(d)) continue;
    if (SB_ONLY_DIMS.has(d) || SP_ONLY_DIMS.has(d)) {
      const surface = SB_ONLY_DIMS.has(d) ? "Sponsored Brands" : "Sponsored Products";
      return {
        ok: false,
        error: "ad_product_dimension_mismatch",
        message: `Dimension ${d} is ${surface}-only and cannot be used with Sponsored Display`,
      };
    }
    if (PURCHASED_PRODUCT_DIMS.has(d)) buckets.add("sdPurchasedProduct");
    else if (ADV_PRODUCT_DIMS.has(d)) buckets.add("sdAdvertisedProduct");
    else if (LEAD_GEN_DIMS.has(d)) buckets.add("sdAdvertisedProduct");
    else if (SD_AD_GROUPS_DIMS.has(d)) buckets.add("sdAdGroups");
    else if (SD_AD_GROUPS_BY_MATCHED_TARGET_DIMS.has(d))
      buckets.add("sdAdGroupsByMatchedTarget");
    else if (SD_TARGETING_DIMS.has(d)) buckets.add("sdTargeting");
    else unknown.push(d);
  }

  if (unknown.length > 0) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Unknown dimension(s) for Sponsored Display: ${unknown.join(", ")}`,
    };
  }

  if (buckets.size > 1) {
    return {
      ok: false,
      error: "unsupported_dimension_combination",
      message: `Dimensions span multiple SD report types (${[...buckets].join(", ")})`,
    };
  }

  if (buckets.size === 1) {
    return { ok: true, reportTypeId: [...buckets][0]!, warnings };
  }

  return { ok: true, reportTypeId: "sdCampaigns", warnings };
}

/** Returns the deduped column list for the SD request. */
export function buildSdColumnList(
  reportTypeId: SdReportTypeId,
  requestedMetricKeys: ReadonlyArray<string>,
  requestedDimensionKeys: ReadonlyArray<string>
): { columns: string[]; warnings: string[] } {
  const warnings: string[] = [];
  const columns = new Set<string>();

  for (const dk of requestedDimensionKeys) {
    if (
      dk === "campaignId" ||
      dk === "campaignName" ||
      dk === "campaignStatus"
    ) {
      columns.add(dk);
      continue;
    }
    const d = SD_V3_DIMENSION_MAP[dk];
    if (!d) {
      warnings.push(`Unknown SD dimension key dropped: ${dk}`);
      continue;
    }
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) {
      warnings.push(
        `Dimension ${dk} valid on ${d.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(d.columnName);
  }

  for (const mk of requestedMetricKeys) {
    const m = SD_V3_COLUMN_MAP[mk];
    if (!m) {
      warnings.push(`Unknown SD metric key dropped: ${mk}`);
      continue;
    }
    if (m.reportTypeId !== reportTypeId) {
      warnings.push(
        `Metric ${mk} valid on ${m.reportTypeId}, dropped for ${reportTypeId}`
      );
      continue;
    }
    columns.add(m.columnName);
  }

  return { columns: [...columns], warnings };
}

/** Returns the deduped `groupBy` values for the SD request body. */
export function buildSdGroupByList(
  reportTypeId: SdReportTypeId,
  requestedDimensionKeys: ReadonlyArray<string>
): string[] {
  const out = new Set<string>();
  for (const dk of requestedDimensionKeys) {
    if (
      dk === "campaignId" ||
      dk === "campaignName" ||
      dk === "campaignStatus"
    ) {
      out.add("campaign");
      continue;
    }
    const d = SD_V3_DIMENSION_MAP[dk];
    if (!d) continue;
    if (d.reportTypeId !== "any" && d.reportTypeId !== reportTypeId) continue;
    out.add(d.groupByValue);
  }
  if (out.size === 0) {
    if (reportTypeId === "sdCampaigns") out.add("campaign");
    else if (reportTypeId === "sdAdvertisedProduct") out.add("advertiser");
    else if (reportTypeId === "sdPurchasedProduct") out.add("purchasedAsin");
  }
  return [...out];
}
